import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Check, X, Calendar, Info, Loader2 } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import SEO, { pageSEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const included = [
  "10 days / 10 nights in top-tier 5-star and palace hotels",
  "Daily breakfast at all properties",
  "Private airport transfers and intercity ground transport",
  "Domestic flights (Jaipur–Dehra Dun–Delhi routing)",
  "Expert local guides throughout the journey",
  "Your host Aaron accompanying the entire trip",
  "Signature included dinners: Bukhara (Delhi) on Day 2, Suvarna Mahal (Jaipur) on Day 5, Ana Rasa (Rishikesh) on Day 8",
  "Lunches on Day 1, Day 3 and Day 4 and Tea/Snacks on Day 7",
  "Cultural show in Delhi",
  "Amber Fort visit with elephant ride logistics",
  "One-hour optional cooking lesson in Jaipur",
  "Car and driver at your disposal for 4 hours on Day 6",
  "Ganga Aarti ceremony experience",
  "Morning yoga session in the Himalayas",
  "All entrance fees to monuments and sites"
];

const notIncluded = [
  "International flights to/from Delhi (from NYC/NJ)",
  "Travel insurance (strongly recommended)",
  "Most lunches and some dinners not specified",
  "Personal expenses and shopping",
  "Tips and gratuities (guidance provided)",
  "Spa treatments and optional wellness activities",
  "Visa fees and processing"
];

export default function PricingPage() {
  const [departures, setDepartures] = useState([]);
  const [searchParams] = useSearchParams();
  const [processingPayment, setProcessingPayment] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const navigate = useNavigate();

  // Stable callback for polling payment status
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingStatus(false);
      toast.error('Payment verification timed out. Please contact us for confirmation.');
      window.history.replaceState({}, '', '/pricing');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setCheckingStatus(false);
        toast.success('Thank you for your deposit to reserve your spot!', {
          duration: 5000,
        });
        window.history.replaceState({}, '', '/pricing');
        setTimeout(() => {
          navigate('/travel-prep');
        }, 2000);
        return;
      } else if (data.status === 'expired') {
        setCheckingStatus(false);
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, '', '/pricing');
        return;
      }

      // Continue polling - use function reference for recursion
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error checking payment status:', error);
      }
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setCheckingStatus(false);
        toast.error('Error verifying payment. Please contact us.');
        window.history.replaceState({}, '', '/pricing');
      }
    }
  }, [navigate]);

  // Check for payment success/cancel on page load
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      setCheckingStatus(true);
      pollPaymentStatus(sessionId);
    } else if (payment === 'cancelled') {
      toast.error('Payment was cancelled. Please try again when ready.');
      window.history.replaceState({}, '', '/pricing');
    }
  }, [searchParams, pollPaymentStatus]);

  // Handle Reserve button click
  const handleReserve = useCallback(async (departureDate) => {
    setProcessingPayment(departureDate);
    
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        departure_date: departureDate,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Payment error:', error);
      }
      toast.error('Failed to initiate payment. Please try again.');
      setProcessingPayment(null);
    }
  }, []);

  // Fetch departures on mount
  useEffect(() => {
    const fetchDepartures = async () => {
      try {
        const res = await axios.get(`${API}/departures`);
        setDepartures(res.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching departures:', error);
        }
      }
    };
    fetchDepartures();
  }, []);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Open':
        return 'badge-open';
      case 'Filling Fast':
        return 'badge-filling';
      case 'Waitlist':
        return 'badge-waitlist';
      case 'Closed':
        return 'badge-closed';
      default:
        return '';
    }
  };

  return (
    <div className="pt-24 md:pt-32" data-testid="pricing-page">
      <SEO {...pageSEO.pricing} />
      {/* Payment Status Modal */}
      {checkingStatus && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-8 max-w-md mx-4 text-center">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-[#243b5a] mb-2">Verifying Payment</h3>
            <p className="text-[#4A4A4A]">Please wait while we confirm your reservation...</p>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="section-padding bg-[#243b5a] text-white">
        <div className="container-max text-center">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Investment</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            Pricing & <span className="text-[#D4AF37]">Inclusions</span>
          </h1>
          <div className="mt-8">
            <span className="font-serif text-6xl md:text-7xl text-white">$6,950</span>
            <span className="text-white/80 text-xl ml-3">per person</span>
          </div>
          <p className="text-white/70 mt-4 text-lg">
            Land only, based on double occupancy. Small group of 6–8 guests.
          </p>
          <p className="text-white/60 text-sm mt-2">
            Single supplement available – please inquire
          </p>
        </div>
      </section>

      {/* Inclusions */}
      <section className="section-padding bg-white" data-testid="inclusions-section">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Included */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="font-serif text-2xl text-[#243b5a]">What's Included</h2>
              </div>
              <ul className="space-y-4">
                {included.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-[#4A4A4A]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Not Included */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <X className="w-6 h-6 text-gray-500" />
                </div>
                <h2 className="font-serif text-2xl text-[#243b5a]">Not Included</h2>
              </div>
              <ul className="space-y-4">
                {notIncluded.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span className="text-[#4A4A4A]">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-4 bg-[#F9F9F7] rounded-lg border border-[#D4AF37]/20">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-[#4A4A4A]">
                    We provide detailed guidance on tipping customs, flight booking from NYC/NJ, and travel insurance recommendations before your trip.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Departure Dates */}
      <section className="section-padding bg-[#F9F9F7]" data-testid="departures-section">
        <div className="container-max">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Book Your Dates</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mt-4">
              Departure Dates & Availability
            </h2>
            <p className="text-[#4A4A4A] mt-4 max-w-2xl mx-auto">
              We keep groups intentionally small (max 8 guests) to maintain a highly personal, hosted experience.
            </p>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block max-w-5xl mx-auto">
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#243b5a] text-white">
                  <tr>
                    <th className="text-left px-6 py-4 font-medium">Dates</th>
                    <th className="text-left px-6 py-4 font-medium">Status</th>
                    <th className="text-left px-6 py-4 font-medium">Availability</th>
                    <th className="text-left px-6 py-4 font-medium">Notes</th>
                    <th className="text-right px-6 py-4 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D4AF37]/10">
                  {departures.map((dep, i) => (
                    <tr key={dep.id} className="hover:bg-[#F9F9F7]/50" data-testid={`departure-row-${i}`}>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-[#D4AF37]" />
                          <span className="font-medium text-[#1A1A1A]">{dep.dates}</span>
                        </div>
                      </td>
                      <td className="px-6 py-5"><span className={getStatusBadgeClass(dep.status)}>{dep.status}</span></td>
                      <td className="px-6 py-5 text-[#4A4A4A]">{dep.availability || '—'}</td>
                      <td className="px-6 py-5 text-[#4A4A4A]">{dep.notes}</td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-end gap-2">
                          {dep.status !== 'Closed' && dep.status !== 'Waitlist' && (
                            <Button 
                              size="sm" 
                              className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] text-sm px-4 py-2"
                              onClick={() => handleReserve(dep.dates)}
                              disabled={processingPayment === dep.dates}
                              data-testid={`reserve-btn-${i}`}
                            >
                              {processingPayment === dep.dates ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                'Reserve'
                              )}
                            </Button>
                          )}
                          {dep.status !== 'Closed' && (
                            <Link to={`/contact?departure=${encodeURIComponent(dep.dates)}`}>
                              <Button size="sm" variant="outline" className="border-[#243b5a] text-[#243b5a] hover:bg-[#243b5a] hover:text-white text-sm px-4 py-2">
                                Inquire
                              </Button>
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {departures.map((dep, i) => (
              <div key={dep.id} className="bg-white rounded-xl p-5 shadow-soft" data-testid={`departure-card-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-[#1A1A1A]">{dep.dates}</span>
                  <span className={getStatusBadgeClass(dep.status)}>{dep.status}</span>
                </div>
                <p className="text-[#4A4A4A] text-sm mb-2">{dep.notes}</p>
                {dep.availability && (
                  <p className="text-[#D4AF37] text-sm font-medium mb-4">{dep.availability}</p>
                )}
                <div className="flex gap-2">
                  {dep.status !== 'Closed' && dep.status !== 'Waitlist' && (
                    <Button 
                      className="btn-gold flex-1 text-sm"
                      onClick={() => handleReserve(dep.dates)}
                      disabled={processingPayment === dep.dates}
                      data-testid={`reserve-btn-mobile-${i}`}
                    >
                      {processingPayment === dep.dates ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Reserve'
                      )}
                    </Button>
                  )}
                  {dep.status !== 'Closed' && (
                    <Link to={`/contact?departure=${encodeURIComponent(dep.dates)}`} className="flex-1">
                      <Button variant="outline" className="btn-secondary w-full text-sm">Inquire</Button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footnotes */}
          <div className="text-center mt-8 text-sm text-[#888888]">
            <p>Arrival Date in India is Departure Date +1 Day. 2027 prices subject to increase by about 12%</p>
            <p>Reserving a Spot requires a token deposit of $1,500, fully refundable until 45 days before departure.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-max text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mb-6">
            Questions About <span className="text-[#D4AF37]">Pricing</span>?
          </h2>
          <p className="text-[#4A4A4A] text-lg max-w-2xl mx-auto mb-8">
            We're happy to discuss payment plans, single supplements, or any other pricing questions.
          </p>
          <Link to="/contact">
            <Button className="btn-gold" data-testid="contact-pricing-btn">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
